/**
 * Analytics Event Definitions
 *
 * Shared event names and property types for consistent tracking
 * across App, Web, and Server.
 *
 * @see docs/ANALYTICS_DESIGN.md
 */

// ============================================
// Event Names (snake_case)
// ============================================

export const AnalyticsEvents = {
  // Session
  SESSION_STARTED: 'session_started',

  // Auth (client-side)
  AUTH_LOGIN: 'auth_login',
  AUTH_LOGOUT: 'auth_logout',

  // Onboarding
  ONBOARDING_COMPLETED: 'onboarding_completed',
  CHILD_PROFILE_SELECTED: 'child_profile_selected',

  // Upload
  UPLOAD_STARTED: 'upload_started',
  UPLOAD_COMPLETED: 'upload_completed',
  UPLOAD_FAILED: 'upload_failed',

  // Task
  TASK_SUMMARY_SHOWN: 'task_summary_shown',
  TASK_SUMMARY_CONFIRMED: 'task_summary_confirmed',
  TASK_OPENED: 'task_opened',
  TASK_SHARED: 'task_shared',
  TASK_COMPLETED: 'task_completed',
  TASK_RETRIED: 'task_retried',
  TASK_ABANDONED: 'task_abandoned',

  // Learning
  LESSON_STARTED: 'lesson_started',
  LESSON_COMPLETED: 'lesson_completed',
  EXERCISE_ANSWERED: 'exercise_answered',
  EXERCISE_HINT_VIEWED: 'exercise_hint_viewed',
  EXERCISE_SET_COMPLETED: 'exercise_set_completed',
  ASSESSMENT_STARTED: 'assessment_started',
  ASSESSMENT_ANSWERED: 'assessment_answered',
  ASSESSMENT_COMPLETED: 'assessment_completed',

  // Conversation
  CONVERSATION_STARTED: 'conversation_started',
  MESSAGE_SENT: 'message_sent',
  ACTION_CLICKED: 'action_clicked',

  // TTS
  TTS_PLAYED: 'tts_played',

  // Feedback
  FEEDBACK_SUBMITTED: 'feedback_submitted',

  // Note: error_occurred removed - use Sentry for error tracking
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// ============================================
// Event Property Types
// ============================================

/** Upload event properties */
export interface UploadEventProperties {
  source?: 'camera' | 'gallery';
  file_size?: number;
  mime_type?: string;
  duration_ms?: number;
  error_code?: string;
  error_message?: string;
}

/** Task event properties */
export interface TaskEventProperties {
  conversation_id?: string;
  task_id?: string;
  public_id?: string;
  subject?: string;
  topic?: string;
  source?: 'direct' | 'share' | 'history'; // 访问来源
  share_method?: 'copy' | 'native'; // 分享方式
}

/** Task abandoned event properties */
export interface TaskAbandonedEventProperties {
  task_id?: string;
  public_id?: string;
  subject?: string;
  phase?: 'ready' | 'lesson' | 'question' | 'celebration'; // 离开时的阶段
  questions_completed?: number; // 已完成题目数
  total_questions?: number; // 总题目数
  progress_percent?: number; // 进度百分比 (0-100)
  duration_ms?: number; // 学习时长
}

/** Task retried event properties (点击"再练一次") */
export interface TaskRetriedEventProperties {
  public_id: string; // 8位公开 ID
  subject?: string; // 科目
  previous_score: number; // 上次得分 (0-100)
  previous_mastery_rate: number; // 上次掌握率 (0-100)
  previous_star_rating: 3 | 4 | 5; // 上次星级
}

/** Feedback event properties */
export interface FeedbackEventProperties {
  task_id?: string;
  public_id?: string;
  feedback_type: 'positive' | 'negative'; // 👍 / 👎
  context?: 'lesson' | 'question' | 'task'; // 反馈上下文
  question_id?: string; // 如果是对特定题目的反馈
}

/** Learning event properties */
export interface LearningEventProperties {
  task_id?: string;
  public_id?: string;
  subject?: string;
  exercise_id?: string;
  assessment_id?: string;
  question_type?: 'single_choice' | 'multiple_choice' | 'fill_blank' | 'true_false';
  difficulty?: 'easy' | 'medium' | 'challenge'; // 🆕 Question difficulty
  is_correct?: boolean;
  attempt_count?: number;
  retry_count?: number; // 🆕 For exercise_retry event
  score?: number;
  current_index?: number;
  total_count?: number;
  correct_count?: number;
  first_try_correct_count?: number; // 🆕 Count of first-try correct answers
  first_try_correct_rate?: number; // 🆕 First-try correct rate (0-100)
  mastery_rate?: number; // 🆕 Mastery rate based on first-try (0-100)
  star_rating?: 3 | 4 | 5; // 🆕 Star rating based on mastery rate
  duration_ms?: number;
}

/** Conversation event properties */
export interface ConversationEventProperties {
  conversation_id?: string;
  has_image?: boolean;
  action_id?: string;
  action_type?: string;
}

// Note: ErrorEventProperties removed - use Sentry for error tracking
